import { mapValues, sortBy } from 'lodash';
import moment from 'moment';
import { brands, light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { MetaWhatsappTemplateStatus, translations } from '../../../../shared/Configs/DatabaseConsts';
import { t } from '../../../../shared/services/i18n';
import { EMAIL, PUSH, SMS, WHATSAPP } from '../../../../shared/services/ConstHelper';
import { IconSwitcher } from '../../../../shared/components/Icons8Generator/IconSwitcher';
import { GlobalConsts } from '../../../../shared/styles/GlobalConsts';
import { Colors } from '../../../../shared/styles/Colors';
import { dbTimeFormat, getBoxTimeFormat, getSessionAutoMessageTags } from '../../../../shared/services/Helpers';

// ---------- Categories ----------

export const AUTO_ACTION_CATEGORY = {
    all: { name: 'autoActionCategoryAll', icon: 'fa-bolt' },
    memberships: { name: 'autoActionCategoryMemberships', icon: 'fa-address-card' },
    leads: { name: 'autoActionCategoryLeads', icon: 'fa-bullseye-arrow' },
    schedule: { name: 'autoActionCategorySchedule', icon: 'fa-calendar' },
    staff: { name: 'autoActionCategoryStaff', icon: 'fa-bell' },
    payments: { name: 'autoActionCategoryPayments', icon: 'fa-circle-dollar' },
    retention: { name: 'autoActionCategoryRetention', icon: 'fa-heart' },
};

const { memberships, leads, schedule, staff, payments, retention } = mapValues(AUTO_ACTION_CATEGORY, (_, key) => key);

const DEFAULT_CATEGORY_ORDER = [memberships, leads, schedule, staff, payments, retention];
const SERVICES_CATEGORY_ORDER = [schedule, staff, leads, payments, memberships, retention];
const CATEGORY_ORDER_BY_SEGMENT = {
    [translations.GROUPS]: [schedule, leads, staff, payments, memberships, retention],
    [translations.CLINICS]: SERVICES_CATEGORY_ORDER,
    [translations.BEAUTY]: SERVICES_CATEGORY_ORDER,
    [translations.COURTS]: SERVICES_CATEGORY_ORDER,
};

export const getOrderedAutoActionCategories = (segment) => CATEGORY_ORDER_BY_SEGMENT[segment] ?? DEFAULT_CATEGORY_ORDER;

export const getAutoActionCategorySelectOptions = (segment, { includeIcons = true } = {}) =>
    ['all', ...getOrderedAutoActionCategories(segment)].map((categoryId) => {
        const category = AUTO_ACTION_CATEGORY[categoryId];
        const option = { value: categoryId, label: t(category.name) };
        if (!includeIcons) return option;
        return {
            ...option,
            icon: IconSwitcher(GlobalConsts.iconPrefix.REGULAR, category.icon),
            selectedIcon: IconSwitcher(GlobalConsts.iconPrefix.SOLID, category.icon),
            ...(categoryId === 'retention' ? { selectedIconColor: Colors.error } : {}),
        };
    });

// ---------- Triggers ----------

const ALL_UNITS = ['MINUTE', 'HOUR', 'DAY', 'WEEK', 'MONTH'];
const DAY_WEEK_MONTH = ['DAY', 'WEEK', 'MONTH'];
export const DEFAULT_TIMING_HOUR = '09:15:00';
const NAME_ONLY_MESSAGE_TAGS = ['first_name', 'last_name'];
const TRIAL_SESSION_MESSAGE_TAGS = ['first_name', 'last_name', 'time', 'end_time', 'date', 'day', 'session_name', 'coach'];
const APPOINTMENT_SESSION_TYPE = 'appointment';

const autoActionsTimeUnits = {
    MINUTE: 'MINUTE',
    HOUR: 'HOUR',
    DAY: 'DAY',
    WEEK: 'WEEK',
    MONTH: 'MONTH',
}

export const AUTO_ACTION_TRIGGERS = {
    RENEW: {
        category: memberships,
        copyKey: 'autoActionTriggerMembershipRenewal',
        immediateLabelKey: 'autoActionTimingImmediateRenew',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    MEMBERSHIP_HOLD: {
        category: memberships,
        copyKey: 'autoActionTriggerMembershipFrozen',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                BEFORE_EVENT: { units: DAY_WEEK_MONTH, sendTime: true },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    CANCELED_MEMBERSHIP: {
        category: memberships,
        copyKey: 'autoActionTriggerMembershipCanceled',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    EXPIRED_MEMBERSHIP: {
        category: memberships,
        copyKey: 'autoActionTriggerMembershipEnded',
        immediateLabelKey: 'autoActionTimingImmediateExpiredMembership',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: DAY_WEEK_MONTH, sendTime: true },
                BEFORE_EVENT: { units: DAY_WEEK_MONTH, sendTime: true },
                IMMEDIATE: { sendTime: true },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    CREATED_LEAD: {
        category: leads,
        copyKey: 'autoActionTriggerLeadCreated',
        immediateLabelKey: 'autoActionTimingImmediateCreatedLead',
        hiddenChannels: [PUSH],
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    CONVERTED_TO_MEMBER: {
        category: leads,
        copyKey: 'autoActionTriggerLeadConverted',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    LOST_LEAD: {
        category: leads,
        copyKey: 'autoActionTriggerLeadLost',
        immediateLabelKey: 'autoActionTimingImmediateLostLead',
        hiddenChannels: [PUSH],
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    BOOK_FOR_TRIAL: {
        category: leads,
        copyKey: 'autoActionTriggerBookedTrialSession',
        applyTo: 'services',
        hiddenChannels: [PUSH],
        messageTags: TRIAL_SESSION_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    BEFORE_TRIAL_CLASS: {
        category: leads,
        copyKey: 'autoActionTriggerBeforeTrialClass',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        hiddenChannels: [PUSH],
    },
    AFTER_TRIAL_CLASS: {
        category: leads,
        copyKey: 'autoActionTriggerAfterTrialClass',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        hiddenChannels: [PUSH],
        checkIn: { labelKey: 'autoActionCheckInOnlyTrial' },
    },
    MISSED_TRIAL_SESSION: {
        category: leads,
        copyKey: 'autoActionTriggerMissedTrialClass',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        hiddenChannels: [PUSH],
        checkIn: { labelKey: 'autoActionCheckInOnlyTrial' },
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    FIRST_SESSION: {
        category: schedule,
        copyKey: 'autoActionTriggerFirstSession',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        checkIn: { labelKey: 'autoActionCheckInOnlySession' },
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                BEFORE_EVENT: { units: ALL_UNITS },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    MISSING_TRAINING: {
        category: schedule,
        copyKey: 'autoActionTriggerMissedSession',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    CLASS_END: {
        category: schedule,
        copyKey: 'autoActionTriggerAtEventEnd',
        applyTo: 'services',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        checkIn: { labelKey: 'autoActionCheckInOnlySession' },
        timing: {
            options: {
                OCCASION_TIME: { countedIn: 'events' },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    REMINDER_SESSION: {
        category: schedule,
        copyKey: 'autoActionTriggerBeforeClassOrCourse',
        applyTo: 'services',
        hiddenServiceTypes: [APPOINTMENT_SESSION_TYPE],
    },
    /** Picker-only entry: saved as REMINDER_SESSION with session_type = appointment. */
    APPOINTMENT_REMINDER_SESSION: {
        category: schedule,
        copyKey: 'autoActionTriggerAppointmentReminder',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
        action: 'REMINDER_SESSION',
    },
    BOOK_SESSION: {
        category: schedule,
        copyKey: 'autoActionTriggerAppointmentBooked',
        immediateLabelKey: 'autoActionTimingImmediateBookSession',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    EDIT_SESSION: {
        category: schedule,
        copyKey: 'autoActionTriggerAppointmentEdited',
        immediateLabelKey: 'autoActionTimingImmediateEditSession',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    CANCEL_SESSION: {
        category: schedule,
        copyKey: 'autoActionTriggerAppointmentCanceled',
        immediateLabelKey: 'autoActionTimingImmediateCancelSession',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    AFTER_SESSION_NOTIFY: {
        category: schedule,
        copyKey: 'autoActionTriggerAfterAppointment',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    MARK_CHECK_IN: {
        category: staff,
        copyKey: 'autoActionTriggerCheckInReminder',
        immediateLabelKey: 'autoActionTimingImmediateMarkCheckIn',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
    },
    BOOK_SESSION_STAFF_NOTIFY: {
        category: staff,
        copyKey: 'autoActionTriggerStaffAppointmentBooked',
        immediateLabelKey: 'autoActionTimingImmediateBookSessionStaffNotify',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    EDIT_SESSION_STAFF_NOTIFY: {
        category: staff,
        copyKey: 'autoActionTriggerStaffAppointmentEdited',
        immediateLabelKey: 'autoActionTimingImmediateEditSessionStaffNotify',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    CANCEL_SESSION_STAFF_NOTIFY: {
        category: staff,
        copyKey: 'autoActionTriggerStaffAppointmentCanceled',
        immediateLabelKey: 'autoActionTimingImmediateCancelSessionStaffNotify',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    AFTER_SESSION_STAFF_NOTIFY: {
        category: staff,
        copyKey: 'autoActionTriggerStaffAfterAppointment',
        applyTo: 'services',
        sessionType: APPOINTMENT_SESSION_TYPE,
        permission: 'appointmentAutoMessages',
    },
    CREDIT_CARD_REFUSAL: {
        category: payments,
        copyKey: 'autoActionTriggerCreditCardDeclined',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME_REFUSAL: { countedIn: 'attempts', sendTime: true },
                OCCASION_TIME: { sendTime: true },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    CREATE_NEW: {
        category: memberships,
        copyKey: 'autoActionTriggerNewClientCreated',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: ALL_UNITS },
                IMMEDIATE: {},
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    BIRTHDAY: {
        category: retention,
        copyKey: 'autoActionTriggerBirthday',
        messageTags: [...NAME_ONLY_MESSAGE_TAGS, 'age'],
        timing: {
            options: {
                OCCASION_TIME: { units: DAY_WEEK_MONTH, sendTime: true },
                BEFORE_EVENT: { units: DAY_WEEK_MONTH, sendTime: true },
                IMMEDIATE: { sendTime: true },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    MISSING: {
        category: retention,
        copyKey: 'autoActionTriggerAbsent',
        immediateLabelKey: 'autoActionTimingImmediateMissing',
        applyTo: 'memberships',
        messageTags: NAME_ONLY_MESSAGE_TAGS,
        timing: {
            options: {
                OCCASION_TIME: { units: DAY_WEEK_MONTH, sendTime: true },
                IMMEDIATE: { sendTime: true },
            },
            defaultCase: 'OCCASION_TIME',
        },
    },
    // Legacy rows with a fixed immediate label but no card copy of their own.
    STATUS_UPDATE: {
        immediateLabelKey: 'autoActionTimingImmediateStatusUpdate',
    },
    NOTIFY_AFTER_PURCHASE: {
        immediateLabelKey: 'autoActionTimingImmediateNotifyAfterPurchase',
    },
};

export const AUTO_ACTION_TRIGGER_KEYS = mapValues(AUTO_ACTION_TRIGGERS, (_, key) => key);

export const getAutoActionTimingOption = (triggerKey, caseValue) =>
    AUTO_ACTION_TRIGGERS[triggerKey]?.timing?.options?.[caseValue];

export const getAutoActionMessageTags = (triggerKey, hasLocations, sessionType) => {
    const configured = AUTO_ACTION_TRIGGERS[triggerKey]?.messageTags;
    const sessionTags = getSessionAutoMessageTags(triggerKey, hasLocations, sessionType);
    if (!configured) return sessionTags;
    const tagPool = [...sessionTags, { id: 'age', label: t('age') }];
    return configured.map((id) => tagPool.find((tag) => tag.id === id)).filter(Boolean);
};

export const getDefaultPeriodAmount = (messageType) => {
    switch (messageType) {
        case AUTO_ACTION_TRIGGER_KEYS.MEMBERSHIP_HOLD:
        case AUTO_ACTION_TRIGGER_KEYS.EXPIRED_MEMBERSHIP:
        case AUTO_ACTION_TRIGGER_KEYS.BIRTHDAY:
        case AUTO_ACTION_TRIGGER_KEYS.MISSING:
            return 1;
        case AUTO_ACTION_TRIGGER_KEYS.REMINDER_SESSION:
        case AUTO_ACTION_TRIGGER_KEYS.BEFORE_TRIAL_CLASS:
        case AUTO_ACTION_TRIGGER_KEYS.AFTER_TRIAL_CLASS:
        case AUTO_ACTION_TRIGGER_KEYS.RENEW:
        case AUTO_ACTION_TRIGGER_KEYS.CANCELED_MEMBERSHIP:
        case AUTO_ACTION_TRIGGER_KEYS.CREATED_LEAD:
        case AUTO_ACTION_TRIGGER_KEYS.CONVERTED_TO_MEMBER:
        case AUTO_ACTION_TRIGGER_KEYS.LOST_LEAD:
        case AUTO_ACTION_TRIGGER_KEYS.BOOK_FOR_TRIAL:
        case AUTO_ACTION_TRIGGER_KEYS.MISSED_TRIAL_SESSION:
        case AUTO_ACTION_TRIGGER_KEYS.CREATE_NEW:
            return 3;
        case AUTO_ACTION_TRIGGER_KEYS.AFTER_SESSION_NOTIFY:
        case AUTO_ACTION_TRIGGER_KEYS.AFTER_SESSION_STAFF_NOTIFY:
            return 5;
        case AUTO_ACTION_TRIGGER_KEYS.MARK_CHECK_IN:
            return 15;
        default:
            return null;
    }
};

export const getDefaultPeriodTimeUnit = (messageType) => {
    switch (messageType) {
        case AUTO_ACTION_TRIGGER_KEYS.MEMBERSHIP_HOLD:
        case AUTO_ACTION_TRIGGER_KEYS.EXPIRED_MEMBERSHIP:
        case AUTO_ACTION_TRIGGER_KEYS.BIRTHDAY:
        case AUTO_ACTION_TRIGGER_KEYS.MISSING:
            return autoActionsTimeUnits.MONTH;
        case AUTO_ACTION_TRIGGER_KEYS.REMINDER_SESSION:
        case AUTO_ACTION_TRIGGER_KEYS.MISSED_TRIAL_SESSION:
        case AUTO_ACTION_TRIGGER_KEYS.BEFORE_TRIAL_CLASS:
        case AUTO_ACTION_TRIGGER_KEYS.AFTER_TRIAL_CLASS:
        case AUTO_ACTION_TRIGGER_KEYS.RENEW:
        case AUTO_ACTION_TRIGGER_KEYS.CANCELED_MEMBERSHIP:
        case AUTO_ACTION_TRIGGER_KEYS.CREATED_LEAD:
        case AUTO_ACTION_TRIGGER_KEYS.CONVERTED_TO_MEMBER:
        case AUTO_ACTION_TRIGGER_KEYS.LOST_LEAD:
        case AUTO_ACTION_TRIGGER_KEYS.BOOK_FOR_TRIAL:
        case AUTO_ACTION_TRIGGER_KEYS.AFTER_SESSION_NOTIFY:
        case AUTO_ACTION_TRIGGER_KEYS.CREATE_NEW:
            return autoActionsTimeUnits.HOUR;
        case AUTO_ACTION_TRIGGER_KEYS.AFTER_SESSION_STAFF_NOTIFY:
            return autoActionsTimeUnits.MINUTE;
        default:
            return null;
    }
};

export const getAutoActionTimingInitialValues = (triggerKey, row) => {
    const timing = AUTO_ACTION_TRIGGERS[triggerKey]?.timing;
    if (!timing) return {};
    let caseValue = timing.defaultCase ?? 'IMMEDIATE';
    if (row) {
        const hasOption = (key) => Boolean(getAutoActionTimingOption(triggerKey, key));
        if (triggerKey === 'CREDIT_CARD_REFUSAL' && row.count != null) {
            caseValue = 'OCCASION_TIME_REFUSAL';
        } else if (row.case === 'IMMEDIATE' && hasOption('IMMEDIATE')) {
            caseValue = 'IMMEDIATE';
        } else if (row.before_after === 'BEFORE' && hasOption('BEFORE_EVENT')) {
            caseValue = 'BEFORE_EVENT';
        } else if (row.case && hasOption(row.case)) {
            caseValue = row.case;
        } else if (row.period_amount === 0 && hasOption('IMMEDIATE')) {
            caseValue = 'IMMEDIATE';
        } else {
            caseValue = 'OCCASION_TIME';
        }
    }
    const option = getAutoActionTimingOption(triggerKey, caseValue) ?? {};
    const units = option.units;
    const defaultAmount = getDefaultPeriodAmount(triggerKey);
    const defaultUnit = getDefaultPeriodTimeUnit(triggerKey);
    const period_amount = caseValue === 'IMMEDIATE' ? (row?.period_amount ?? 0) : (row?.period_amount ?? defaultAmount ?? 1);
    const period_time_unit = row?.period_time_unit && units?.includes(row.period_time_unit)
        ? row.period_time_unit
        : (defaultUnit && units?.includes(defaultUnit) ? defaultUnit : (units?.[0] ?? null));
    const hour = option.sendTime ? (row?.hour ?? DEFAULT_TIMING_HOUR) : null;
    let count = null;
    if (caseValue === 'OCCASION_TIME_REFUSAL') count = row?.count ?? 1;
    return { case: caseValue, period_amount, period_time_unit, hour, count };
};

// ---------- Relations (apply to memberships / services) ----------

const AUTO_ACTION_RELATION_BY_APPLY_TO = {
    memberships: { relatableType: 'membership_type', saveKey: 'membership_type_ids', rowKey: 'auto_actions_set_relation_membership_types' },
    services: { relatableType: 'box_category', saveKey: 'box_category_ids', rowKey: 'auto_actions_set_relation_box_categories' },
};

export const getAutoActionApplyToRelation = (applyTo) => {
    switch (applyTo) {
        case 'memberships':
            return {
                title: 'autoActionApplyToMemberships',
                scopeAll: 'autoActionRelationAllMemberships',
                scopeSpecific: 'autoActionRelationSpecificMemberships',
                selectPlaceholder: 'autoActionSelectMemberships',
                specificHelper: 'autoActionApplyToSpecificMembershipsHelper',
                allHelper: 'autoActionApplyToAllMembershipsHelper',
                scopeOne: 'autoActionRelationOneMembership',
                scopeCount: 'autoActionRelationCountMemberships',
            };
        case 'services':
            return {
                title: 'autoActionApplyToServices',
                scopeAll: 'autoActionRelationAllServices',
                scopeSpecific: 'autoActionRelationSpecificServices',
                selectPlaceholder: 'autoActionSelectServices',
                specificHelper: 'autoActionApplyToSpecificServicesHelper',
                allHelper: 'autoActionApplyToAllServicesHelper',
                scopeOne: 'autoActionRelationOneService',
                scopeCount: 'autoActionRelationCountServices',
            };
        default:
            return null;
    }
};

const getAutoActionRelationConfig = (actionName) => AUTO_ACTION_RELATION_BY_APPLY_TO[AUTO_ACTION_TRIGGERS[actionName]?.applyTo];

const getAutoActionRelationItemsFromRow = (row, actionName) => {
    const relationConfig = getAutoActionRelationConfig(actionName);
    if (!relationConfig || !row) return [];
    return (row[relationConfig.rowKey] ?? [])
        .map((entity) => ({ id: entity.relatable?.id, name: entity.relatable?.name }))
        .filter((item) => item.id && item.name);
};

export const buildAutoActionRelationSaveParams = (values) => {
    const relationConfig = getAutoActionRelationConfig(values.actionName);
    if (!relationConfig) return {};
    const selectedIds = values.relation_kind === 'specific' ? (values.relation_items ?? []).map((item) => item.id) : [];
    return { [relationConfig.saveKey]: selectedIds };
};

export const getAutoActionRelationFormValues = (rows, actionName) => {
    const items = getAutoActionRelationItemsFromRow(rows?.[0], actionName);
    return { relation_kind: items.length ? 'specific' : 'all', relation_items: items };
};

export const buildAutoActionRelationFromRow = (row, actionName) => {
    const items = getAutoActionRelationItemsFromRow(row, actionName);
    if (!items.length) return { kind: 'all', items: [] };
    return { kind: getAutoActionRelationConfig(actionName).relatableType, items };
};

export const buildAutoActionTimingParams = (triggerKey, values) => {
    const formCase = values.case;
    const option = getAutoActionTimingOption(triggerKey, formCase) ?? {};
    const hasAmountInput = Boolean(option.units || option.countedIn);
    const params = { case: formCase === 'IMMEDIATE' ? 'IMMEDIATE' : 'OCCASION_TIME' };
    if (formCase === 'IMMEDIATE') params.before_after = null;
    else if (formCase === 'BEFORE_EVENT') params.before_after = 'BEFORE';
    else params.before_after = 'AFTER';
    params.period_amount = formCase === 'IMMEDIATE' ? 0 : (hasAmountInput ? values.period_amount : 0);
    if (option.units) params.period_time_unit = values.period_time_unit;
    params.hour = option.sendTime ? values.hour : null;
    if (triggerKey === 'CREDIT_CARD_REFUSAL') {
        if (formCase === 'OCCASION_TIME_REFUSAL') params.count = values.count;
        else if (formCase === 'OCCASION_TIME') params.count = null;
    }
    return params;
};

export const getTriggerName = (autoAction) => {
    const action = autoAction?.action ?? autoAction?.auto_actions?.action;
    if (action !== 'LEAD_ATTENDANCE') {
        return action;
    }
    return autoAction?.before_after === 'AFTER' ? 'AFTER_TRIAL_CLASS' : 'BEFORE_TRIAL_CLASS';
};

export const getSessionTypeForTrigger = (triggerKey) => AUTO_ACTION_TRIGGERS[triggerKey]?.sessionType ?? null;

export const getMessageTypeAutoActions = (autoActions, triggerKey, sessionType) =>
    (autoActions ?? []).filter((row) => getTriggerName(row) === triggerKey && row.session_type === sessionType);

export const getAutoActionCopyKey = (autoAction) => {
    if (autoAction?.copy_key) {
        return autoAction.copy_key;
    }
    const triggerName = getTriggerName(autoAction);
    if (triggerName === 'REMINDER_SESSION' && autoAction?.session_type === 'appointment') {
        return 'autoActionTriggerAppointmentReminder';
    }
    return AUTO_ACTION_TRIGGERS[triggerName]?.copyKey;
};

export const getAutoActionTitle = (autoAction) => {
    const copyKey = getAutoActionCopyKey(autoAction);
    return copyKey ? t(`${copyKey}Title`) : '';
};

export const getAutoActionDescription = (autoAction) => {
    const copyKey = getAutoActionCopyKey(autoAction);
    return copyKey ? t(`${copyKey}Description`) : '';
};

export const getAutoActionCategory = (autoAction) =>
    AUTO_ACTION_TRIGGERS[getTriggerName(autoAction)]?.category ?? autoAction?.category ?? null;

export const AUTO_ACTION_CHANNELS = {
    [WHATSAPP]: { icon: brands('whatsapp') },
    [EMAIL]: { icon: light('envelope'), chipStyle: { color: Colors.newDesign.purpleText, backgroundColor: Colors.newDesign.lightLightPurple } },
    [PUSH]: { icon: light('mobile'), chipStyle: { color: Colors.newDesign.darkOrange, backgroundColor: Colors.newDesign.paleYellow } },
    [SMS]: { icon: light('comment'), chipStyle: { color: Colors.newDesign.mainBlue, backgroundColor: Colors.newDesign.lightBlueBackground } },
};

const WHATSAPP_CHIP_STYLE_BY_STATUS = {
    [MetaWhatsappTemplateStatus.APPROVED]: { color: Colors.autoActionsColors.mediumGreen, backgroundColor: Colors.autoActionsColors.paleGreen },
    [MetaWhatsappTemplateStatus.PENDING]: { color: Colors.newDesign.mainText, backgroundColor: Colors.newDesign.avatarBgColor },
    [MetaWhatsappTemplateStatus.NOT_APPROVED]: { color: Colors.newDesign.redTagText, backgroundColor: Colors.newDesign.redTagBackground },
};

export const getChannelIconChipStyle = (channelId, whatsappStatus) => {
    if (channelId === WHATSAPP) {
        return WHATSAPP_CHIP_STYLE_BY_STATUS[whatsappStatus] ?? WHATSAPP_CHIP_STYLE_BY_STATUS[MetaWhatsappTemplateStatus.NOT_APPROVED];
    }
    return AUTO_ACTION_CHANNELS[channelId]?.chipStyle ?? {};
};

const CHANNEL_DISPLAY_ORDER = Object.keys(AUTO_ACTION_CHANNELS);
const channelDisplayRank = (channelId) => {
    const rank = CHANNEL_DISPLAY_ORDER.indexOf(channelId);
    return rank === -1 ? CHANNEL_DISPLAY_ORDER.length : rank;
};

export const sortAutoActionChannels = (channelIds = []) => sortBy(channelIds, channelDisplayRank);

// ---------- Filters ----------

export const AUTO_ACTION_STATUS = { active: 'active', inactive: 'inactive', all: 'all' };
export const AUTO_ACTION_CHANNEL = { all: 'all', whatsapp: WHATSAPP, push: PUSH, email: EMAIL, sms: SMS };
export const AUTO_ACTION_SORT = { category: 'category', name: 'name', updated: 'updated' };

export const AUTO_ACTION_FILTER_DEFAULTS = {
    search: '',
    status: AUTO_ACTION_STATUS.active,
    channel: AUTO_ACTION_CHANNEL.all,
    category: 'all',
    // Empty = no sort picked yet (trigger shows "Sort"); the list still orders by category.
    sort: '',
};

export const FILTER_LABEL_KEYS = {
    status: {
        [AUTO_ACTION_STATUS.active]: 'autoActionsSegmentedActive',
        [AUTO_ACTION_STATUS.inactive]: 'autoActionsSegmentedInactive',
        [AUTO_ACTION_STATUS.all]: 'autoActionsSegmentedAll',
    },
    channel: {
        [AUTO_ACTION_CHANNEL.all]: 'autoActionChannelAll',
        [AUTO_ACTION_CHANNEL.whatsapp]: 'autoActionChannelWhatsapp',
        [AUTO_ACTION_CHANNEL.push]: 'autoActionChannelPush',
        [AUTO_ACTION_CHANNEL.email]: 'autoActionChannelEmail',
        [AUTO_ACTION_CHANNEL.sms]: 'autoActionChannelSms',
    },
    sort: { [AUTO_ACTION_SORT.category]: 'autoActionSortCategory', [AUTO_ACTION_SORT.name]: 'autoActionSortName', [AUTO_ACTION_SORT.updated]: 'autoActionSortUpdated' },
};

const toFilterOptions = (filterKey, values) => values.map((value) => ({ value, label: t(FILTER_LABEL_KEYS[filterKey][value]) }));

export const getAutoActionChannelValues = (hasWhatsappIntegration) =>
    Object.values(AUTO_ACTION_CHANNEL).filter((channel) => channel !== AUTO_ACTION_CHANNEL.whatsapp || hasWhatsappIntegration);

const DROPDOWN_FILTER_PROPS = { showBorder: true, styleOverride: { height: '30px' } };

export const getAutoActionFiltersConfig = ({ hasWhatsappIntegration, sortTitle }) => [
    {
        key: 'status',
        type: 'segmented',
        show: true,
        defaultValue: AUTO_ACTION_FILTER_DEFAULTS.status,
        sectionTitle: t('status'),
        options: toFilterOptions('status', Object.values(AUTO_ACTION_STATUS)),
        props: { className: 'auto-actions-status-segment' },
    },
    {
        key: 'channel',
        type: 'dropdown',
        show: true,
        defaultValue: AUTO_ACTION_FILTER_DEFAULTS.channel,
        sectionTitle: t('channel'),
        placeholder: t('autoActionChannelAll'),
        options: toFilterOptions('channel', getAutoActionChannelValues(hasWhatsappIntegration)),
        props: DROPDOWN_FILTER_PROPS,
    },
    {
        key: 'sort',
        type: 'dropdown',
        show: true,
        defaultValue: AUTO_ACTION_FILTER_DEFAULTS.sort,
        sectionTitle: t('sort'),
        title: sortTitle,
        options: toFilterOptions('sort', Object.values(AUTO_ACTION_SORT)),
        props: DROPDOWN_FILTER_PROPS,
    },
];

export const getAutoActionMobileFiltersConfig = ({ hasWhatsappIntegration, sortTitle }) =>
    getAutoActionFiltersConfig({ hasWhatsappIntegration, sortTitle }).map((cfg) => {
        if (cfg.key === 'channel') {
            const options = cfg.options.map((option) => option.value === AUTO_ACTION_CHANNEL.all
                ? option
                : { ...option, selectedStyle: getChannelIconChipStyle(option.value, MetaWhatsappTemplateStatus.APPROVED) });
            return { ...cfg, type: 'chips', options, props: { testIDPrefix: 'auto-action-channel-chip' } };
        }
        if (cfg.key === 'sort') return { ...cfg, type: 'chips', props: { testIDPrefix: 'auto-action-sort-chip' } };
        return cfg;
    });

export const isAutoActionFiltersAtDefault = (filters) =>
    Object.keys(AUTO_ACTION_FILTER_DEFAULTS).every((key) => (filters[key] ?? '') === AUTO_ACTION_FILTER_DEFAULTS[key]);

// ---------- Card chips ----------

export const formatAutoActionTimingDisplay = (timing, action) => {
    if (timing?.case === 'IMMEDIATE' || timing?.period_amount === 0) {
        return { label: t(AUTO_ACTION_TRIGGERS[action]?.immediateLabelKey ?? 'immediateAutomaticAction'), hour: null };
    }
    const { before_after, period_amount, period_time_unit, hour } = timing ?? {};
    const unit = action === 'CLASS_END' ? t('classes') : t(`${period_time_unit}automaticActionTable`);
    const amount = `${period_amount} ${unit} `;
    const label = before_after === 'AFTER' ? `${t('afterAutomticActionTable')} ${amount}` : `${amount}${t('before')}`;
    const hourStr = hour ? moment(hour, dbTimeFormat).format(getBoxTimeFormat() || 'HH:mm') : null;
    return { label: hourStr ? t('autoActionTimingAtHour', { timing: "After 1 weeks", hour: "11:15 AM" }) : label, hour: hourStr };
};

const MEMBERSHIP_RELATABLE_TYPES = ['membership_type', 'memberships'];

const isMembershipRelation = (relation) =>
    MEMBERSHIP_RELATABLE_TYPES.includes(relation?.kind) ||
    MEMBERSHIP_RELATABLE_TYPES.includes(relation?.target) ||
    (relation?.kind === 'all' && relation?.target !== 'box_category' && relation?.entity_kind === 'membership_type');

export const formatAutoActionRelationChip = (relation, action) => {
    // Use the action's applyTo if available, otherwise infer from relation
    const applyTo = action && AUTO_ACTION_TRIGGERS[action]?.applyTo
        ? AUTO_ACTION_TRIGGERS[action].applyTo
        : (isMembershipRelation(relation) ? 'memberships' : 'services');
    const applyToRelation = getAutoActionApplyToRelation(applyTo);
    if (!applyToRelation) {
        return { label: '', tooltip: null };
    }
    if (!relation || relation.kind === 'all') {
        return { label: t(applyToRelation.scopeAll), tooltip: null };
    }
    const items = relation.items ?? [];
    const names = items.map((item) => item?.name).filter(Boolean);
    const count = items.length;
    const labelKey = count === 1 ? applyToRelation.scopeOne : applyToRelation.scopeCount;
    return { label: t(labelKey, { count }), tooltip: names.length ? names.join(', ') : null };
};